import React from "react";
import { Link, redirect, useLoaderData } from "react-router-dom";

import classNames from "classnames";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import invariant from "tiny-invariant";

import ejectionReportSchema from "./lib/EjectionReportSchema";
import { EjectionReportFormData } from "./types/ejectionreport";

import { store } from "../../..";
import { alertActions } from "../../../features/alert/alertSlice";
import { selectUser } from "../../../features/auth/authSlice";
import {
    globalActions,
    selectSite,
} from "../../../features/global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks/reduxHooks";
import { createEjection, getEjectionReport } from "../../../lib/api";
import {
    defaultDateFormat,
    defaultTimeFormat,
    displayFormAlert,
} from "../../../lib/utils";
import { IEjectionReport } from "../../../types/api";

export const loader = async ({ params }: any) => {
    const { gameId } = params;
    const user = selectUser(store.getState());
    invariant(user);
    const { sites } = store.getState().global;

    const promises = [];
    !sites.length && promises.push(store.dispatch(globalActions.getSites()));

    const [{ report }] = await Promise.all([
        getEjectionReport(gameId),
        ...promises,
    ]);

    if (!report.umpires?.some(u => u.id === user.id)) {
        return redirect("/my-schedule/past");
    }

    return {
        report,
    };
};

const EjectionReport = (): React.JSX.Element => {
    const { report } = useLoaderData() as { report: IEjectionReport };
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    invariant(user);
    const site = useAppSelector(state => selectSite(state, report.site));

    const gameDate = defaultDateFormat.format(new Date(report.date));
    const gameTime = defaultTimeFormat.format(new Date(report.date));

    const {
        handleSubmit,
        register,
        formState: { errors },
    } = useForm<EjectionReportFormData>({
        mode: "onSubmit",
        resolver: yupResolver(ejectionReportSchema),
        defaultValues: {
            gameId: report.id,
            firstName: report?.firstName,
            lastName: report?.lastName,
            report: report?.report,
        },
    });

    const onSubmit: SubmitHandler<EjectionReportFormData> = async data => {
        await createEjection(data, user.id)
            .then(() => {
                dispatch(
                    alertActions.success({
                        title: "Ejection Report submitted successfully",
                        dismissible: true,
                    }),
                );
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: "The server encountered a problem submitting the ejection report.",
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            });
    };

    const onError: SubmitErrorHandler<EjectionReportFormData> = data => {
        console.log(data);
    };

    return (
        <Container className={classNames("mt-4", "mb-5")}>
            <Link to="/my-schedule/past">Back to games list</Link>
            <h4 className={classNames("mt-2")}>
                Ejection Report for{" "}
                {`${report.visitor.name} @ ${report.home.name} - ${gameDate}, ${gameTime}`}
            </h4>
            <h5>Site: {site?.displayName}</h5>
            <Form
                onSubmit={handleSubmit(onSubmit, onError)}
                id="ejectionReportForm"
            >
                <Row>
                    <Form.Label className={classNames("mb-0")}>
                        Name of Ejected Person (if known)
                    </Form.Label>
                    <Form.Group as={Col} md={6}>
                        <Form.Control type="text" {...register("firstName")} />
                        <Form.Label>First Name</Form.Label>
                    </Form.Group>
                    <Form.Group as={Col} md={6}>
                        <Form.Control type="text" {...register("lastName")} />
                        <Form.Label>Last Name</Form.Label>
                    </Form.Group>
                </Row>
                <Row>
                    <Col>
                        <Form.Label className={classNames("mb-0")}>
                            Report *
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={6}
                            {...register("report", {
                                required: "Please submit a report",
                            })}
                        />
                        {errors.report &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.report.message as string,
                            })}
                    </Col>
                </Row>
                <Row>
                    <Col xs={2}>
                        <Button type="submit">Submit Report</Button>
                    </Col>
                </Row>
            </Form>
        </Container>
    );
};

export default EjectionReport;
